/* eslint-disable react/no-unescaped-entities */
/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Paper, Snackbar, Button, Box, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Info as InfoIcon,
  AttachMoney as MoneyIcon,
  EmojiEvents as AwardIcon,
  Warning as WarningIcon,
  Description as DescriptionIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Payment as PaymentIcon,
} from '@material-ui/icons';
import MuiAlert from '@material-ui/lab/Alert';
import uuid from 'uuid/v4';
import { useAuth } from '../../../../auth/authProvider';
import request from '../../../../services/network/request';
import partnershipRequest from '../../../../services/network/partnershipRequest';
import { ComissionBonusCondition } from '../../../../helpers/enums';
import useStylesFromFile, { stepIconStyles } from './CadastroPlanos.styles';
import InfosBasicas from './InfosBasicas';
import Calculo from './Calculo';
import Pagamento from './Pagamento';
import Bonus from './Bonus';
import Penalidades from './Penalidades';
import Resumo from './Resumo';

const StepConnectorLine = ({ status }) => {
  const getLineColor = () => {
    if (status === 'completed') {
      return '#4caf50';
    }
    if (status === 'warning') {
      return '#FF5246';
    }
    return '#e0e0e0';
  };

  return (
    <Box
      style={{
        flex: 1,
        height: 3,
        backgroundColor: getLineColor(),
        borderRadius: 2,
        margin: '0 8px',
        transition: 'background-color 0.3s ease',
      }}
    />
  );
};

const useStepIconStyles = makeStyles(stepIconStyles);

const stepIcons = {
  0: <InfoIcon style={{ fontSize: 20 }} />,
  1: <MoneyIcon style={{ fontSize: 20 }} />,
  2: <PaymentIcon style={{ fontSize: 20 }} />,
  3: <AwardIcon style={{ fontSize: 20 }} />,
  4: <WarningIcon style={{ fontSize: 20 }} />,
  5: <DescriptionIcon style={{ fontSize: 20 }} />,
};

const CustomStepIcon = props => {
  const stepClasses = useStepIconStyles();
  const { active, completed, icon, warning, pending, onClick } = props;

  const getClassName = () => {
    const classNames = [stepClasses.root];
    if (active) {
      classNames.push(stepClasses.active);
    } else if (completed) {
      classNames.push(stepClasses.completed);
    } else if (warning) {
      classNames.push(stepClasses.warning);
    } else if (pending) {
      classNames.push(stepClasses.pending);
    }
    return classNames.join(' ');
  };

  return (
    <div className={getClassName()} onClick={onClick} role="button" tabIndex={0} onKeyPress={onClick}>
      {completed ? <CheckIcon style={{ fontSize: 20 }} /> : stepIcons[icon - 1]}
    </div>
  );
};

const useStyles = makeStyles(useStylesFromFile);

const CadastroPlanos = ({ onCancel, onSuccess, planId }) => {
  const classes = useStyles();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [aggregateBonusGroups, setAggregateBonusGroups] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    distributorIds: [],
    calculationType: 'scale',
    fixedType: 'fixed',
    fixedValue: '',
    scales: [{ id: Date.now(), minValue: '0', maxValue: '', percent: '' }],
    payment: {
      hasInitialPayment: false,
      initialPaymentType: 'percent',
      initialPaymentValue: '',
      hasInvoicePayment: false,
      invoicePaymentType: 'percent',
      invoicePaymentValue: '',
      hasRecurrence: false,
      recurrenceType: 'percent',
      recurrenceValue: '',
    },
    bonuses: [],
    penalties: [],
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      distributorIds: [],
      calculationType: 'scale',
      fixedType: 'fixed',
      fixedValue: '',
      scales: [{ minValue: '0', maxValue: '', percent: '' }],
      payment: {
        hasInitialPayment: false,
        initialPaymentType: 'percent',
        initialPaymentValue: '',
        hasInvoicePayment: false,
        invoicePaymentType: 'percent',
        invoicePaymentValue: '',
        hasRecurrence: false,
        recurrenceType: 'percent',
        recurrenceValue: '',
      },
      bonuses: [],
      penalties: [],
    });
    setAggregateBonusGroups([]);
  };

  const { data: distributorsData = [], isLoading: loadingDistributors } = useQuery(
    'fetch-distribuidoras-qualifying-gd',
    async () => {
      return request.get('parcerias/distribuidoras/qualifying-gd', { token });
    },
  );

  const distributors = distributorsData || [];

  const transformPlanDataToFormData = plan => {
    // Determinar tipo de cálculo a partir do backend
    const calculationType = plan.calculationType || 'scale';

    // Transformar escalas
    const scales =
      plan.comissionScales && plan.comissionScales.length > 0
        ? plan.comissionScales.map(scale => ({
            id: Date.now() + Math.random(),
            minValue: scale.minKWhVolume?.toString() || '0',
            maxValue: scale.maxKWhVolume?.toString() || '',
            percent: scale.comissionPercentage ? (scale.comissionPercentage * 100).toFixed(2) : '',
          }))
        : [{ id: Date.now(), minValue: '0', maxValue: '', percent: '' }];

    // Transformar fixedType e fixedValue do backend
    const fixedType = plan.comissionType === 'percentage' ? 'percent' : plan.comissionType || 'fixed';
    const fixedValue = plan.comissionValue?.toString() || '';

    // Transformar pagamento
    const payment = plan.comissionPayment || {};
    const normalizePaymentType = type => {
      if (!type) {
        return 'percent';
      }
      return type === 'percentage' ? 'percent' : type;
    };

    // Transformar bônus
    const bonuses = (plan.comissionBonus || []).map(bonus => ({
      aggregateBonusId: bonus.aggregateBonusId || null,
      name: bonus.name || '',
      bonusTrigger: bonus.bonusTrigger?.toString() || '',
      value: bonus.value?.toString() || '',
      condition: bonus.condition || ComissionBonusCondition.QUANTIDADE_MINIMA_KWH,
    }));

    // Identificar grupos de bônus agregados
    const bonusGroupsMap = new Map();
    bonuses.forEach(bonus => {
      if (bonus.aggregateBonusId) {
        if (!bonusGroupsMap.has(bonus.aggregateBonusId)) {
          bonusGroupsMap.set(bonus.aggregateBonusId, {
            id: bonus.aggregateBonusId,
            name: `Grupo ${bonusGroupsMap.size + 1}`,
          });
        }
      }
    });
    setAggregateBonusGroups(Array.from(bonusGroupsMap.values()));

    // Transformar penalidades
    const penalties = (plan.comissionPenalties || []).map(penalty => ({
      id: Date.now() + Math.random(),
      name: penalty.name || '',
      type: penalty.reason || '',
      value: penalty.value?.toString() || '',
      unit: penalty.type || 'percentage',
    }));

    // Transformar distribuidoras
    const distributorIds = (plan.tarifaDistribuidoras || []).map(d => d.id);

    setFormData({
      name: plan.name || '',
      description: plan.description || '',
      distributorIds,
      calculationType,
      fixedType,
      fixedValue,
      scales,
      payment: {
        hasInitialPayment: payment.hasInitialPayment || false,
        initialPaymentType: normalizePaymentType(payment.initialPaymentType) || 'percent',
        initialPaymentValue: payment.initialPaymentValue?.toString() || '',
        hasInvoicePayment: payment.hasInvoicePayment || false,
        invoicePaymentType: normalizePaymentType(payment.invoicePaymentType) || 'percent',
        invoicePaymentValue: payment.invoicePaymentValue?.toString() || '',
        hasRecurrence: payment.hasRecurrence || false,
        recurrenceType: normalizePaymentType(payment.recurrenceType) || 'percent',
        recurrenceValue: payment.recurrenceValue?.toString() || '',
      },
      bonuses,
      penalties,
    });
  };

  // Buscar dados do plano quando for edição
  const { data: planDataFromApi, isLoading: loadingPlanData } = useQuery(
    ['fetch-comission-plan', planId],
    async () => {
      if (!planId) {
        return null;
      }
      return partnershipRequest.get(`comissions/comission-plans/${planId}`, { token });
    },
    {
      enabled: !!planId,
    },
  );

  // Transformar dados do plano quando carregados
  useEffect(() => {
    if (planDataFromApi && planId) {
      transformPlanDataToFormData(planDataFromApi);
    }
  }, [planDataFromApi, planId]);

  const [createPlan, { status: createPlanStatus }] = useMutation(
    async planDataPayload => {
      if (planId) {
        return partnershipRequest.patch(`comissions/comission-plans/${planId}`, planDataPayload, { token });
      }
      return partnershipRequest.post('comissions/comission-plans', planDataPayload, { token });
    },
    {
      onSuccess: () => {
        setShowSuccessMessage(true);
        setTimeout(() => {
          resetForm();
          setActiveTab(0);
          if (onSuccess) {
            onSuccess();
          }
        }, 2000);
      },
      onError: error => {
        const message = error || `Erro ao ${planId ? 'atualizar' : 'salvar'} plano de comissionamento`;
        setErrorMessage(message);
        setShowErrorMessage(true);
      },
    },
  );

  const getDistributorNames = () => {
    return distributors.filter(d => formData.distributorIds.includes(d.id)).map(d => d.name);
  };

  const validateField = (fieldName, value, formDataContext = formData) => {
    const errors = { ...fieldErrors };

    switch (fieldName) {
      case 'name':
        if (!value || !value.trim()) {
          errors.name = 'Nome do plano é obrigatório';
        } else {
          delete errors.name;
        }
        break;
      case 'distributorIds':
        if (!value || value.length === 0) {
          errors.distributorIds = 'Selecione pelo menos uma distribuidora';
        } else {
          delete errors.distributorIds;
        }
        break;
      case 'fixedValue':
        if (formDataContext.calculationType === 'fixed' && (!value || value.trim() === '')) {
          errors.fixedValue = 'Valor fixo é obrigatório';
        } else {
          delete errors.fixedValue;
        }
        break;
      case 'scales': {
        if (formDataContext.calculationType === 'scale') {
          // Verificar se todas as escalas têm os campos obrigatórios preenchidos
          const scalesWithErrors = formDataContext.scales
            .map((scale, index) => {
              const scaleErrors = [];
              if (!scale.minValue || scale.minValue.trim() === '') {
                scaleErrors.push(`Faixa ${index + 1}: Volume mínimo é obrigatório`);
              }
              if (!scale.maxValue || scale.maxValue.trim() === '') {
                scaleErrors.push(`Faixa ${index + 1}: Volume máximo é obrigatório`);
              }
              if (!scale.percent || scale.percent.trim() === '') {
                scaleErrors.push(`Faixa ${index + 1}: Percentual é obrigatório`);
              }
              return scaleErrors.length > 0 ? scaleErrors : null;
            })
            .filter(Boolean);

          if (scalesWithErrors.length > 0) {
            errors.scales = scalesWithErrors.flat().join('. ');
          } else {
            const validScales = formDataContext.scales.filter(s => s.minValue && s.maxValue && s.percent);
            if (validScales.length === 0) {
              errors.scales = 'Configure pelo menos uma faixa de escalonamento';
            } else {
              const sortedScales = [...validScales]
                .map(s => ({
                  min: parseFloat(s.minValue),
                  max: parseFloat(s.maxValue),
                }))
                .sort((a, b) => a.min - b.min);

              if (sortedScales[0].min !== 0) {
                errors.scales = 'A primeira faixa de escalonamento deve começar em 0 kWh';
              } else {
                let gapError = null;
                for (let i = 0; i < sortedScales.length - 1; i++) {
                  const currentMax = sortedScales[i].max;
                  const nextMin = sortedScales[i + 1].min;

                  if (nextMin !== currentMax) {
                    gapError = `Gap nas faixas: faixa termina em ${currentMax} kWh mas a próxima começa em ${nextMin} kWh.`;
                    break;
                  }
                }
                if (gapError) {
                  errors.scales = gapError;
                } else {
                  delete errors.scales;
                }
              }
            }
          }
        } else {
          delete errors.scales;
        }
        break;
      }
      default:
        break;
    }

    setFieldErrors(errors);
  };

  const handleDistributorToggle = (distributorId, isChecked) => {
    setFormData(prev => {
      const newDistributorIds = isChecked
        ? [...prev.distributorIds, distributorId]
        : prev.distributorIds.filter(id => id !== distributorId);
      const newFormData = { ...prev, distributorIds: newDistributorIds };
      // Validar após atualizar o estado
      setTimeout(() => {
        validateField('distributorIds', newDistributorIds, newFormData);
      }, 0);
      return newFormData;
    });
  };

  const addScale = () => {
    setFormData({
      ...formData,
      scales: [...formData.scales, { id: Date.now(), minValue: '', maxValue: '', percent: '' }],
    });
  };

  const removeScale = index => {
    const newScales = formData.scales.filter((_, i) => i !== index);
    setFormData({ ...formData, scales: newScales });
  };

  const updateScale = (index, field, value) => {
    const newScales = [...formData.scales];
    newScales[index][field] = value;
    setFormData({ ...formData, scales: newScales });
  };

  /**
   * Valida se bônus agregados usam apenas condições permitidas (quantidade mínima).
   * Condições "A cada N" são multiplicativas e não funcionam como faixas.
   */
  const validateAggregateBonusCondition = (bonuses, groups) => {
    if (!bonuses || bonuses.length === 0) {
      return null;
    }

    for (const bonus of bonuses) {
      if (bonus.aggregateBonusId && !ComissionBonusCondition.isAggregateAllowed(bonus.condition)) {
        const groupName = groups.find(g => g.id === bonus.aggregateBonusId)?.name || bonus.aggregateBonusId;
        return (
          `Condição "${bonus.condition}" não é permitida para bônus em grupo "${groupName}". ` +
          `Bônus agregados só podem usar as condições: ` +
          `"${ComissionBonusCondition.QUANTIDADE_MINIMA_KWH}" ou ` +
          `"${ComissionBonusCondition.QUANTIDADE_MINIMA_CONTRATOS}". ` +
          `Condições "A cada N" são multiplicativas e não funcionam como faixas.`
        );
      }
    }

    return null;
  };

  /**
   * Valida se todos os bônus de um mesmo grupo agregado utilizam o mesmo critério.
   */
  const validateAggregateBonusCriteria = (bonuses, groups) => {
    if (!bonuses || bonuses.length === 0) {
      return null;
    }

    const aggregateGroupsMap = new Map();

    for (const bonus of bonuses) {
      if (bonus.aggregateBonusId) {
        const existingCondition = aggregateGroupsMap.get(bonus.aggregateBonusId);

        if (existingCondition === undefined) {
          aggregateGroupsMap.set(bonus.aggregateBonusId, bonus.condition);
        } else if (existingCondition !== bonus.condition) {
          const groupName = groups.find(g => g.id === bonus.aggregateBonusId)?.name || bonus.aggregateBonusId;
          return (
            `Critério inconsistente no grupo "${groupName}": ` +
            `todos os bônus do grupo devem usar o mesmo critério. ` +
            `Encontrado "${existingCondition}" e "${bonus.condition}"`
          );
        }
      }
    }

    return null;
  };

  const addBonus = (aggregateId = null) => {
    setFormData({
      ...formData,
      bonuses: [
        ...formData.bonuses,
        {
          aggregateBonusId: aggregateId,
          name: '',
          bonusTrigger: '',
          value: '',
          condition: ComissionBonusCondition.QUANTIDADE_MINIMA_KWH,
        },
      ],
    });
  };

  const removeBonus = index => {
    const newBonuses = formData.bonuses.filter((_, i) => i !== index);
    setFormData({ ...formData, bonuses: newBonuses });
  };

  const updateBonus = (index, field, value) => {
    const newBonuses = [...formData.bonuses];
    newBonuses[index][field] = value;

    // Atualizar o estado primeiro
    setFormData({ ...formData, bonuses: newBonuses });

    // Mostrar erro se houver inconsistência (o botão será desabilitado automaticamente)
    if (field === 'condition' && newBonuses[index].aggregateBonusId) {
      const conditionError = validateAggregateBonusCondition(newBonuses, aggregateBonusGroups);
      if (conditionError) {
        setErrorMessage(conditionError);
        setShowErrorMessage(true);
        return;
      }

      const criteriaError = validateAggregateBonusCriteria(newBonuses, aggregateBonusGroups);
      if (criteriaError) {
        setErrorMessage(criteriaError);
        setShowErrorMessage(true);
      }
    }
  };

  const addPenalty = () => {
    setFormData({
      ...formData,
      penalties: [
        ...formData.penalties,
        {
          id: Date.now(),
          name: '',
          type: '',
          value: '',
          unit: 'percentage',
        },
      ],
    });
  };

  const removePenalty = index => {
    const newPenalties = formData.penalties.filter((_, i) => i !== index);
    setFormData({ ...formData, penalties: newPenalties });
  };

  const updatePenalty = (index, field, value) => {
    const newPenalties = [...formData.penalties];
    newPenalties[index][field] = value;
    setFormData({ ...formData, penalties: newPenalties });
  };

  const createBonusGroup = () => {
    const groupId = uuid();
    const groupName = `Grupo ${aggregateBonusGroups.length + 1}`;
    setAggregateBonusGroups([...aggregateBonusGroups, { id: groupId, name: groupName }]);
  };

  const removeGroup = groupId => {
    setAggregateBonusGroups(aggregateBonusGroups.filter(g => g.id !== groupId));
    setFormData({
      ...formData,
      bonuses: formData.bonuses.filter(b => b.aggregateBonusId !== groupId),
    });
  };

  const getBonusesByGroup = groupId => {
    return formData.bonuses
      .map((bonus, index) => ({ ...bonus, originalIndex: index }))
      .filter(bonus => bonus.aggregateBonusId === groupId);
  };

  const getIndividualBonuses = () => {
    return formData.bonuses
      .map((bonus, index) => ({ ...bonus, originalIndex: index }))
      .filter(bonus => !bonus.aggregateBonusId);
  };

  const normalizePaymentType = type => {
    if (!type) {
      return null;
    }
    return type === 'percent' ? 'percentage' : type;
  };

  const transformToDTO = () => {
    const getInitialPaymentType = () => {
      if (!formData.payment.hasInitialPayment) {
        return null;
      }
      return normalizePaymentType(formData.payment.initialPaymentType);
    };

    const getRecurrenceType = () => {
      if (!formData.payment.hasRecurrence) {
        return null;
      }
      return normalizePaymentType(formData.payment.recurrenceType);
    };

    const getInvoicePaymentType = () => {
      if (!formData.payment.hasInvoicePayment) {
        return null;
      }
      return normalizePaymentType(formData.payment.invoicePaymentType);
    };

    // Determinar comissionType baseado no fixedType do formulário
    const getComissionType = () => {
      if (formData.fixedType === 'percent') {
        return 'percentage';
      }
      return 'fixed';
    };

    // Base do DTO
    const dto = {
      name: formData.name,
      description: formData.description || '',
      calculationType: formData.calculationType,
      comissionPayment: {
        hasInitialPayment: formData.payment.hasInitialPayment,
        initialPaymentType: getInitialPaymentType(),
        initialPaymentValue: formData.payment.hasInitialPayment
          ? parseFloat(formData.payment.initialPaymentValue) || 0
          : null,
        hasInvoicePayment: formData.payment.hasInvoicePayment,
        invoicePaymentType: getInvoicePaymentType(),
        invoicePaymentValue: formData.payment.hasInvoicePayment
          ? parseFloat(formData.payment.invoicePaymentValue) || 0
          : null,
        hasRecurrence: formData.payment.hasRecurrence,
        ...(formData.payment.hasRecurrence && {
          recurrenceType: getRecurrenceType(),
          recurrenceValue: parseFloat(formData.payment.recurrenceValue) || 0,
        }),
      },
      comissionBonus: formData.bonuses
        .filter(
          b =>
            b.bonusTrigger !== '' &&
            b.bonusTrigger !== null &&
            b.bonusTrigger !== undefined &&
            b.value !== '' &&
            b.value !== null &&
            b.value !== undefined &&
            b.condition,
        )
        .map(b => ({
          aggregateBonusId: b.aggregateBonusId || null,
          name: b.name || '',
          bonusTrigger: parseFloat(b.bonusTrigger),
          value: parseFloat(b.value),
          condition: b.condition,
        })),
      comissionPenalties: formData.penalties
        .filter(p => p.type && p.value)
        .map(p => ({
          reason: p.type,
          name: p.name || '',
          value: parseFloat(p.value),
          type: p.unit,
        })),
      tarifaDistribuidoras: formData.distributorIds.map(id => ({ id })),
    };

    // Adicionar campos condicionais baseado no calculationType
    if (formData.calculationType === 'fixed') {
      // Para FIXED: enviar comissionType, comissionValue e comissionScales vazio
      dto.comissionType = getComissionType();
      dto.comissionValue = parseFloat(formData.fixedValue) || 0;
      dto.comissionScales = [];
    } else {
      // Para SCALE: enviar apenas comissionScales (sem comissionType e comissionValue)
      dto.comissionScales = formData.scales
        .filter(s => s.minValue && s.maxValue && s.percent)
        .map(s => ({
          minKWhVolume: parseFloat(s.minValue),
          maxKWhVolume: parseFloat(s.maxValue),
          comissionPercentage: parseFloat(s.percent) / 100,
        }))
        .sort((a, b) => a.minKWhVolume - b.minKWhVolume);
    }

    return dto;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Nome do plano é obrigatório';
    }

    if (formData.distributorIds.length === 0) {
      errors.distributorIds = 'Selecione pelo menos uma distribuidora';
    }

    if (formData.calculationType === 'fixed') {
      if (!formData.fixedValue) {
        errors.fixedValue = 'Valor fixo é obrigatório';
      }
    } else {
      // Verificar se todas as escalas têm os campos obrigatórios preenchidos
      const scalesWithErrors = formData.scales
        .map((scale, index) => {
          const scaleErrors = [];
          if (!scale.minValue || scale.minValue.trim() === '') {
            scaleErrors.push(`Faixa ${index + 1}: Volume mínimo é obrigatório`);
          }
          if (!scale.maxValue || scale.maxValue.trim() === '') {
            scaleErrors.push(`Faixa ${index + 1}: Volume máximo é obrigatório`);
          }
          if (!scale.percent || scale.percent.trim() === '') {
            scaleErrors.push(`Faixa ${index + 1}: Percentual é obrigatório`);
          }
          return scaleErrors.length > 0 ? scaleErrors : null;
        })
        .filter(Boolean);

      if (scalesWithErrors.length > 0) {
        errors.scales = scalesWithErrors.flat().join('. ');
      } else {
        const validScales = formData.scales.filter(s => s.minValue && s.maxValue && s.percent);
        if (validScales.length === 0) {
          errors.scales = 'Configure pelo menos uma faixa de escalonamento';
        } else {
          const sortedScales = [...validScales]
            .map(s => ({
              min: parseFloat(s.minValue),
              max: parseFloat(s.maxValue),
            }))
            .sort((a, b) => a.min - b.min);

          if (sortedScales[0].min !== 0) {
            errors.scales = 'A primeira faixa de escalonamento deve começar em 0 kWh';
          } else {
            for (let i = 0; i < sortedScales.length - 1; i++) {
              const currentMax = sortedScales[i].max;
              const nextMin = sortedScales[i + 1].min;

              if (nextMin !== currentMax) {
                errors.scales = `Gap nas faixas de escalonamento: faixa termina em ${currentMax} kWh mas a próxima começa em ${nextMin} kWh. O valor máximo de uma faixa DEVE ser igual ao valor mínimo da próxima.`;
                break;
              }
            }
          }
        }
      }
    }

    // Validar condição permitida para bônus agregados
    const aggregateConditionError = validateAggregateBonusCondition(formData.bonuses, aggregateBonusGroups);
    if (aggregateConditionError) {
      errors.bonuses = aggregateConditionError;
    }

    // Validar critério consistente nos bônus agregados
    if (!errors.bonuses) {
      const aggregateCriteriaError = validateAggregateBonusCriteria(formData.bonuses, aggregateBonusGroups);
      if (aggregateCriteriaError) {
        errors.bonuses = aggregateCriteriaError;
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0 ? null : errors[Object.keys(errors)[0]];
  };

  const handleSubmit = e => {
    if (e) {
      e.preventDefault();
    }

    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      setShowErrorMessage(true);
      return;
    }

    const dto = transformToDTO();
    createPlan(dto);
  };

  const handleCancel = () => {
    resetForm();
    setActiveTab(0);
    if (onCancel) {
      onCancel();
    }
  };

  const handleCloseSnackbar = () => {
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
  };

  const steps = [
    { id: 0, label: 'Informações Básicas' },
    { id: 1, label: 'Cálculo' },
    { id: 2, label: 'Pagamento' },
    { id: 3, label: 'Bônus' },
    { id: 4, label: 'Penalidades' },
    { id: 5, label: 'Resumo' },
  ];

  const stepValidation = useMemo(() => {
    const distributorNames = distributors.filter(d => formData.distributorIds.includes(d.id)).map(d => d.empresa);

    return {
      0: formData.name && formData.name.trim() !== '' && distributorNames.length > 0,
      1:
        formData.calculationType === 'fixed'
          ? formData.fixedValue && formData.fixedValue !== ''
          : formData.scales.length > 0 && formData.scales.some(s => s.percent),
      2: true,
      3: true,
      4: true,
      5: true,
    };
  }, [formData, distributors]);

  const isNextButtonDisabled = useMemo(() => {
    // Desabilitar o botão "Próximo" quando estiver na etapa de bônus e houver inconsistência
    if (activeTab === 3) {
      return (
        validateAggregateBonusCondition(formData.bonuses, aggregateBonusGroups) !== null ||
        validateAggregateBonusCriteria(formData.bonuses, aggregateBonusGroups) !== null
      );
    }
    return false;
  }, [activeTab, formData.bonuses, aggregateBonusGroups]);

  const [visitedSteps, setVisitedSteps] = useState({ 0: true });

  const handleStepClick = stepIndex => {
    if (stepIndex <= activeTab || visitedSteps[stepIndex] || stepIndex === activeTab + 1) {
      // Validar bônus agregados antes de sair da etapa de bônus (step 3)
      if (activeTab === 3 && stepIndex !== 3) {
        const conditionError = validateAggregateBonusCondition(formData.bonuses, aggregateBonusGroups);
        if (conditionError) {
          setErrorMessage(conditionError);
          setShowErrorMessage(true);
          return;
        }

        const criteriaError = validateAggregateBonusCriteria(formData.bonuses, aggregateBonusGroups);
        if (criteriaError) {
          setErrorMessage(criteriaError);
          setShowErrorMessage(true);
          return;
        }
      }

      setActiveTab(stepIndex);
      setVisitedSteps(prev => ({ ...prev, [stepIndex]: true }));
    }
  };

  const handleNext = () => {
    // Validar bônus agregados antes de sair da etapa de bônus (step 3)
    if (activeTab === 3) {
      const conditionError = validateAggregateBonusCondition(formData.bonuses, aggregateBonusGroups);
      if (conditionError) {
        setErrorMessage(conditionError);
        setShowErrorMessage(true);
        return;
      }

      const criteriaError = validateAggregateBonusCriteria(formData.bonuses, aggregateBonusGroups);
      if (criteriaError) {
        setErrorMessage(criteriaError);
        setShowErrorMessage(true);
        return;
      }
    }

    if (activeTab < steps.length - 1) {
      const nextStep = activeTab + 1;
      setActiveTab(nextStep);
      setVisitedSteps(prev => ({ ...prev, [nextStep]: true }));
    }
  };

  const handlePrev = () => {
    if (activeTab > 0) {
      setActiveTab(activeTab - 1);
    }
  };

  const getStepStatus = stepIndex => {
    if (stepIndex === activeTab) {
      return 'active';
    }
    if (stepIndex < activeTab) {
      if (stepValidation[stepIndex]) {
        return 'completed';
      }
      return 'warning';
    }
    return 'pending';
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <InfosBasicas
            formData={formData}
            setFormData={setFormData}
            distributors={distributors}
            loadingDistributors={loadingDistributors}
            handleDistributorToggle={handleDistributorToggle}
            classes={classes}
            fieldErrors={fieldErrors}
            validateField={validateField}
          />
        );
      case 1:
        return (
          <Calculo
            formData={formData}
            setFormData={setFormData}
            addScale={addScale}
            removeScale={removeScale}
            updateScale={updateScale}
            classes={classes}
            fieldErrors={fieldErrors}
            validateField={validateField}
          />
        );
      case 2:
        return <Pagamento formData={formData} setFormData={setFormData} classes={classes} />;
      case 3:
        return (
          <Bonus
            formData={formData}
            addBonus={addBonus}
            removeBonus={removeBonus}
            updateBonus={updateBonus}
            createBonusGroup={createBonusGroup}
            removeGroup={removeGroup}
            aggregateBonusGroups={aggregateBonusGroups}
            getBonusesByGroup={getBonusesByGroup}
            getIndividualBonuses={getIndividualBonuses}
            classes={classes}
          />
        );
      case 4:
        return (
          <Penalidades
            formData={formData}
            addPenalty={addPenalty}
            removePenalty={removePenalty}
            updatePenalty={updatePenalty}
            classes={classes}
          />
        );
      case 5:
        return (
          <Resumo
            formData={formData}
            getDistributorNames={getDistributorNames}
            handleSubmit={handleSubmit}
            createPlanStatus={createPlanStatus}
            handleCancel={handleCancel}
            classes={classes}
          />
        );
      default:
        return null;
    }
  };

  if (loadingPlanData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {onCancel && (
        <Box marginBottom={2} marginLeft={3} marginTop={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            style={{
              borderColor: '#FF5246',
              color: '#FF5246',
              backgroundColor: 'rgba(255, 82, 70, 0.08)',
              textTransform: 'none',
              fontWeight: 600,
              padding: '10px 20px',
              borderRadius: 8,
              borderWidth: 2,
              transition: 'all 0.2s ease-in-out',
              boxShadow: '0 1px 3px rgba(255, 82, 70, 0.1)',
            }}
            onMouseOver={e => {
              e.currentTarget.style.backgroundColor = '#FF5246';
              e.currentTarget.style.color = '#ffffff';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 82, 70, 0.3)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 82, 70, 0.08)';
              e.currentTarget.style.color = '#FF5246';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(255, 82, 70, 0.1)';
            }}
            onFocus={() => {}}
          >
            Voltar para Listagem
          </Button>
        </Box>
      )}
      <Paper elevation={3} className={classes.paper}>
        <Box
          style={{
            padding: '24px 32px 16px',
            backgroundColor: '#fff',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {steps.map((step, index) => {
              const status = getStepStatus(index);
              const isClickable = index <= activeTab || visitedSteps[index] || index === activeTab + 1;
              const prevStatus = index > 0 ? getStepStatus(index - 1) : null;

              return (
                <React.Fragment key={step.id}>
                  {index > 0 && (
                    <Box
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        height: 44,
                      }}
                    >
                      <StepConnectorLine status={prevStatus} />
                    </Box>
                  )}
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      cursor: isClickable ? 'pointer' : 'default',
                      minWidth: 80,
                    }}
                    onClick={() => isClickable && handleStepClick(index)}
                  >
                    <CustomStepIcon
                      icon={index + 1}
                      active={status === 'active'}
                      completed={status === 'completed'}
                      warning={status === 'warning'}
                      pending={status === 'pending'}
                      onClick={() => isClickable && handleStepClick(index)}
                    />
                    <Typography
                      variant="caption"
                      style={{
                        fontWeight: activeTab === index ? 600 : 400,
                        color: (() => {
                          if (status === 'warning') {
                            return '#FF5246';
                          }
                          if (status === 'completed') {
                            return '#4caf50';
                          }
                          if (status === 'active') {
                            return '#FF5246';
                          }
                          return '#666';
                        })(),
                        fontSize: 12,
                        textAlign: 'center',
                        marginTop: 8,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 12,
              gap: 8,
            }}
          >
            <Typography variant="caption" style={{ color: '#999' }}>
              Etapa {activeTab + 1} de {steps.length}
            </Typography>
          </Box>
        </Box>

        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          <div className={classes.formContent}>{renderTabContent()}</div>

          {activeTab !== 5 && (
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '16px 24px',
                borderTop: '1px solid #e0e0e0',
                backgroundColor: '#fafafa',
              }}
            >
              <Button
                variant="outlined"
                startIcon={<PrevIcon />}
                onClick={handlePrev}
                disabled={activeTab === 0}
                style={{
                  textTransform: 'none',
                  borderColor: activeTab === 0 ? '#e0e0e0' : '#666',
                  color: activeTab === 0 ? '#bbb' : '#666',
                }}
              >
                Anterior
              </Button>
              <Button
                variant="contained"
                endIcon={<NextIcon />}
                onClick={handleNext}
                disabled={isNextButtonDisabled}
                style={{
                  textTransform: 'none',
                  backgroundColor: isNextButtonDisabled ? '#ccc' : '#FF5246',
                  color: '#fff',
                  cursor: isNextButtonDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                {activeTab === 4 ? 'Revisar e Salvar' : 'Próximo'}
              </Button>
            </Box>
          )}
        </div>
      </Paper>

      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert onClose={handleCloseSnackbar} severity="success" elevation={6} variant="filled">
          Plano de comissionamento {planId ? 'atualizado' : 'salvo'} com sucesso!
        </MuiAlert>
      </Snackbar>

      <Snackbar
        open={showErrorMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert onClose={handleCloseSnackbar} severity="error" elevation={6} variant="filled">
          {errorMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default CadastroPlanos;
